import React from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon, EyeIcon, RotateCcwIcon, UploadCloudIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
interface PublishBarProps {
  hasChanges: boolean;
  changedSections: string[];
  lastPublishedAt: string | null;
  justPublished: boolean;
  onPublish: () => void;
  onDiscard: () => void;
}
function formatStamp(value: string | null): string {
  if (!value) return 'Not published from here yet';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not published from here yet';
  return `Live version published ${date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric'
  })} at ${date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit'
  })}`;
}
export function PublishBar({
  hasChanges,
  changedSections,
  lastPublishedAt,
  justPublished,
  onPublish,
  onDiscard
}: PublishBarProps) {
  return <Card className="p-4" data-id="element-1146" data-mp="2kr5746856u00">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between" data-id="element-1147" data-mp="zrsl9z7n0dt0">
        <div className="min-w-0" data-id="element-1148" data-mp="14qn0ysab5m8w">
          <div className="flex flex-wrap items-center gap-2.5" data-id="element-1149" data-mp="14plqoq1493s8">
            <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11.5px] font-medium ring-1 ring-inset ${hasChanges ? 'bg-amber-500/10 text-warn ring-amber-500/25' : 'bg-emerald-500/10 text-emerald-300 ring-emerald-500/25'}`} data-id="element-1150" data-mp="19pq7kh5xs0iz">
              <span className="h-1.5 w-1.5 rounded-full bg-current" data-id="element-1151" data-mp="m0ni4n5293dx" />
              {hasChanges ? 'Draft has unpublished changes' : 'Live site is up to date'}
            </span>
            <span className="text-[11.5px] text-zinc-600" data-id="element-1152" data-mp="vq74w3vw9he2">{formatStamp(lastPublishedAt)}</span>
          </div>

          <p className="mt-2 max-w-xl text-[12.5px] leading-relaxed text-zinc-400" data-id="element-1153" data-mp="3uf5vn4drtb4x">
            {hasChanges ? 'Everything you edit here stays in the draft. Fans keep seeing the live version until you publish.' : 'The draft and the live site match. Make an edit and it lands in the draft first.'}
          </p>

          {hasChanges && changedSections.length > 0 && <div className="mt-2.5 flex flex-wrap gap-1.5" data-id="element-1154" data-mp="4xq6e012ue72">
              {changedSections.map(section => <span key={section} className="rounded-md bg-white/[0.05] px-2 py-1 text-[11.5px] font-medium text-zinc-300" data-id="element-1155" data-mp="6rqz0gvzel">
                  {section}
                </span>)}
            </div>}
        </div>

        <div className="flex shrink-0 flex-wrap gap-2" data-id="element-1156" data-mp="3iefo2kodkxpu">
          <Link to="/studio/preview" data-id="element-1157" data-mp="3deuny6xgyjbj">
            <Button variant="secondary" size="sm" data-id="element-1158" data-mp="3ddry82bvumx7">
              <EyeIcon className="h-3.5 w-3.5" data-id="element-1159" data-mp="xnf6mb5yqa7m" /> Preview draft site
            </Button>
          </Link>
          {hasChanges && <Button variant="ghost" size="sm" onClick={onDiscard} data-id="element-1160" data-mp="sp593twrmxiu">
              <RotateCcwIcon className="h-3.5 w-3.5" data-id="element-1161" data-mp="107v1q9rcydvg" /> Discard draft
            </Button>}
          <Button variant="primary" size="sm" onClick={onPublish} disabled={!hasChanges && !justPublished} className="bg-[#d97742] shadow-none hover:bg-[#e08d59] disabled:opacity-40" data-id="element-1162" data-mp="156y7xxj2qq50">
            {justPublished ? <CheckIcon className="h-3.5 w-3.5" data-id="element-1163" data-mp="vauu3uev1328" /> : <UploadCloudIcon className="h-3.5 w-3.5" data-id="element-1164" data-mp="1ixum4jc974sj" />}
            {justPublished ? 'Published' : 'Publish to live site'}
          </Button>
        </div>
      </div>
    </Card>;
}